import { skuSuffix, slugify } from '@telestore/shared';

/**
 * Retail variant helpers (CLAUDE.md §4: the v1 extension is size × colour with
 * per-variant stock).
 *
 * The matrix generator exists because the alternative — a merchant hand-adding
 * 18 rows for a blouse in 6 sizes and 3 colours — is exactly the friction that
 * makes them go back to Excel.
 */

export interface VariantDraft {
  size: string | null;
  color: string | null;
  colorHex: string | null;
  sku: string;
  stock: number;
  position: number;
}

export interface MatrixInput {
  sizes: readonly string[];
  colors: readonly { name: string; hex?: string | null }[];
  /** Applied to every generated cell; merchants adjust afterwards. */
  defaultStock?: number;
  /** Prefix for generated SKUs, usually derived from the product name. */
  skuPrefix?: string;
}

export function buildVariantMatrix(input: MatrixInput): VariantDraft[] {
  const sizes = dedupe(input.sizes);
  const colors = input.colors.filter(
    (color, index, all) => all.findIndex((c) => normalize(c.name) === normalize(color.name)) === index,
  );
  const stock = input.defaultStock ?? 0;
  const prefix = input.skuPrefix ? skuPrefixOf(input.skuPrefix) : null;

  // A product with neither axis still needs one sellable row.
  if (sizes.length === 0 && colors.length === 0) {
    return [{ size: null, color: null, colorHex: null, sku: makeSku(prefix, null, null), stock, position: 0 }];
  }

  const drafts: VariantDraft[] = [];
  const sizeAxis = sizes.length > 0 ? sizes : [null];
  const colorAxis = colors.length > 0 ? colors : [null];

  let position = 0;
  for (const color of colorAxis) {
    for (const size of sizeAxis) {
      drafts.push({
        size,
        color: color?.name ?? null,
        colorHex: color?.hex ?? null,
        sku: makeSku(prefix, size, color?.name ?? null),
        stock,
        position: position++,
      });
    }
  }
  return drafts;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function dedupe(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === '') continue;
    const key = normalize(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function skuPrefixOf(name: string): string {
  const slug = slugify(name, { fallback: 'empty' }).replace(/-/g, '').toUpperCase();
  return slug.slice(0, 6) || 'ITEM';
}

function makeSku(prefix: string | null, size: string | null, color: string | null): string {
  const parts = [prefix ?? 'ITEM'];
  if (color) parts.push(shortToken(color));
  if (size) parts.push(shortToken(size));
  parts.push(skuSuffix());
  return parts.join('-');
}

function shortToken(value: string): string {
  const cleaned = slugify(value, { fallback: 'empty' }).replace(/-/g, '').toUpperCase();
  return (cleaned || 'X').slice(0, 4);
}

/**
 * Sizes sort by garment convention, not alphabetically — a merchant seeing
 * "L, M, S, XL, XS" in their admin loses confidence immediately.
 */
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL', 'FREE', 'ONE SIZE'];

export function compareSizes(a: string, b: string): number {
  const indexA = SIZE_ORDER.indexOf(a.trim().toUpperCase());
  const indexB = SIZE_ORDER.indexOf(b.trim().toUpperCase());
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;

  // Numeric sizes (shoes, waist) sort numerically.
  const numA = Number.parseFloat(a);
  const numB = Number.parseFloat(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;

  return a.localeCompare(b);
}

export interface StockSummary {
  total: number;
  inStockVariants: number;
  outOfStockVariants: number;
  low: boolean;
}

export function summarizeStock(
  variants: readonly { stock: number; isActive: boolean }[],
  lowThreshold: number,
): StockSummary {
  const active = variants.filter((v) => v.isActive);
  const total = active.reduce((sum, v) => sum + v.stock, 0);
  return {
    total,
    inStockVariants: active.filter((v) => v.stock > 0).length,
    outOfStockVariants: active.filter((v) => v.stock <= 0).length,
    low: total > 0 && total <= lowThreshold,
  };
}

/** The "from" price on a product card: the cheapest sellable variant. */
export function displayPrice(
  basePrice: number,
  variants: readonly { price: number | null; isActive: boolean; stock: number }[],
): { price: number; isFrom: boolean } {
  const sellable = variants.filter((v) => v.isActive);
  if (sellable.length === 0) return { price: basePrice, isFrom: false };
  const prices = sellable.map((v) => v.price ?? basePrice);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { price: min, isFrom: min !== max };
}
